import {Component, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {IModel} from "../../../../../shared/view-model/imodel.interface";
import {BarcodeService} from "../../../../../identity/services/barcode.service";
import {Subject} from "rxjs/Subject";
import {ErrorHandlerService} from "../../../error-handling/error-handler.service";
import {ApolloError} from "apollo-client";
import {SessionActions} from "../../../../../shared/actions/session.actions";
import {MessageType} from "../../../../../shared/redux-store/session/session.types";
import {ApolloQueryObservable} from "apollo-angular";
import {Subscription} from "rxjs/Subscription";
import {HasPermissionToGuard} from '../../../authorization/guards/has-permission-to.guard';
import {ACCESS_MOBILE_SCANNER} from '../../../../../shared/auth/guards/api-permission';

@Component({
  selector: 'vb-barcode-scanner',
  templateUrl: './barcode-scanner.component.html',
  styleUrls: ['./barcode-scanner.component.css']
})
export class BarcodeScannerComponent implements OnInit, OnDestroy {

  @HostBinding('class')
  styleClass: string;

  @Input()
  iconClass: string;

  @Input()
  withTrashed = false;

  @Input()
  enabled: boolean = true;

  private enableRead: boolean = false;
  hasError: boolean = false;

  isLoading: boolean = false;

  @HostListener('document:keydown', ['$event'])
  handleStartEvent(event: KeyboardEvent) {

    if (!this.enabled && this.isStartKey(event)) {
      event.preventDefault();
      this.error("Die Code-Erfassung ist derzeit deaktiviert, bitte aktivieren Sie sie vor dem Scannen!");
      return;
    }

    // Init barcode
    if (!this.enableRead && this.isStartKey(event)) {
      event.preventDefault();
      this.enableRead = true;
      this.ean = '';
      return;
    }

    if (this.enableRead && event.key != "Enter") {
      event.preventDefault();
      this.ean += event.key;
      return;
    }

    if (this.enableRead && event.key.startsWith('Shift')) {
      event.preventDefault();
      return;
    }

    // Start search
    if (this.enableRead && event.key == "Enter") {
      event.preventDefault();
      this.enableRead = false;
      this.ean$.next(this.ean);
      this.ean = '';
      return;
    }

  }

  @HostListener('document:keypress', ['$event'])
  handleKeyboardPressEvent(event: KeyboardEvent) {
    if (this.enableRead) {
      event.preventDefault();
    }

  }

  private ean: string = "";
  private ean$: Subject<string> = new Subject();
  private eanSub: Subscription;

  @Input()
  allowedTypes: string[];

  @Input()
  forceSynchronousProcessing: boolean = false;

  @Output()
  searchFinished: EventEmitter<IModel> = new EventEmitter();

  private barcodesQuery$: ApolloQueryObservable<any>;
  private barcodesSubscription: Subscription;

  private errorSound = new Audio("assets/sounds/error-sound.wav");
  private successSound = new Audio("assets/sounds/beep.mp3");

  canAccessMobileScanner = false;

  constructor(private barcodeService: BarcodeService, private err: ErrorHandlerService, private sessionActions: SessionActions, private accessMobileScannerGuard: HasPermissionToGuard) {
    this.errorSound.load();
    this.successSound.load();
  }

  onMobileEanRecognized(ean) {
    this.ean$.next(ean);
    this.playSuccessSound();
  }

  ngOnInit() {

    this.canAccessMobileScanner = this.accessMobileScannerGuard.hasPermissionTo(ACCESS_MOBILE_SCANNER);

    this.barcodesQuery$ = this.barcodeService.cacheBarcodes(null, this.withTrashed);
    this.barcodesSubscription = this.barcodesQuery$.subscribe();

    this.eanSub = this.ean$.subscribe(ean => {
      if (this.forceSynchronousProcessing) {
        console.error("Synchronous execution not yet supported!");
      } else {
        this.searchforItem(ean);
      }

    });
  }

  ngOnDestroy(): void {

    if (this.eanSub) {
      this.eanSub.unsubscribe();
    }

    if (this.barcodesSubscription) {
      this.barcodesSubscription.unsubscribe();
    }

  }

  private isStartKey(event: KeyboardEvent): boolean {
    return (event.keyCode == 45 || event.key == "Insert" || event.key == "#");
  }

  searchforItem(ean: string) {

    this.isLoading = true;

    this.barcodeService.resolveEAN(ean, this.withTrashed).toPromise()
      .then(res => {
        let bc = res.data.barcode;
        this.isLoading = false;

        if (!bc) {
          this.error("Es wurde kein zum Barcode " + ean + " passendes Objekt gefunden!");
        } else if (bc && !bc.identifiable) {
          this.error("Dem Barcode " + ean + " ist kein Objekt zugeordnet!");
        } else {
          if (this.allowedTypes) {
            if (this.allowedTypes.find(t => t.toLocaleLowerCase() == bc.identifiable.__typename.toLowerCase())) {
              this.success(bc);
            } else {
              this.error("Der Typ des durch den Barcode identifizierten Objekts ist an dieser Stelle nicht zulässig!");
            }
          } else {
            this.success(bc);
          }
        }

      })
      .catch(error => {
        this.isLoading = false;

        if ((error instanceof ApolloError) && error.message.startsWith("GraphQL error: No query results")) {
          this.error("Es wurde kein zum Barcode " + ean + " passendes Objekt gefunden!");
        } else {
          this.error("Ein Fehler ist aufgetreten, bitte versuchen Sie es erneut!", error)
        }

      });

    this.ean = '';

  }

  error(msg: string, err = null) {
    this.playErrorSound();
    this.hasError = true;
    if (err) {
      this.err.handleUserError(err, msg);
    } else {
      this.sessionActions.pushMessage(msg, MessageType.WARNING, "Barcode-Eingabe");
    }
  }

  success(bc) {
    this.hasError = false;
    this.searchFinished.emit(bc.identifiable);
  }

  private playErrorSound() {
    this.errorSound.play();
  }

  playSuccessSound() {
    this.successSound.play();
  }

}



// WEBPACK FOOTER //
// ./src/app/core/visbos-core/ui/components/barcode-scanner/barcode-scanner.component.ts